import React, { useState } from "react";
import { SortAsc, SortDesc, Grid, List } from "lucide-react";
import { JobPosting } from "../../types/job";
import JobCard from "./JobCard";

interface JobListProps {
  jobs: JobPosting[];
  onBookmark: (jobId: string) => void;
  searchQuery: string;
}

type SortOption = "relevance" | "date" | "salary" | "applicants";
type ViewMode = "grid" | "list";

const JobList: React.FC<JobListProps> = ({ jobs, onBookmark, searchQuery }) => {
  const [sortBy, setSortBy] = useState<SortOption>("relevance");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [viewMode, setViewMode] = useState<ViewMode>("grid");
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 12;

  const sortJobs = (jobs: JobPosting[]) => {
    return [...jobs].sort((a, b) => {
      let comparison = 0;

      switch (sortBy) {
        case "date":
          comparison =
            new Date(a.postedAt).getTime() - new Date(b.postedAt).getTime();
          break;
        case "salary":
          const aSalary = parseInt(
            a.salary.split("-")[0].replace(/[^0-9]/g, "")
          );
          const bSalary = parseInt(
            b.salary.split("-")[0].replace(/[^0-9]/g, "")
          );
          comparison = aSalary - bSalary;
          break;
        case "applicants":
          comparison = a.applicants - b.applicants;
          break;
        case "relevance":
        default:
          // 검색어와의 관련성을 기반으로 정렬 (간단한 구현)
          const aRelevance = searchQuery
            ? (a.title.toLowerCase().includes(searchQuery.toLowerCase())
                ? 3
                : 0) +
              (a.company.toLowerCase().includes(searchQuery.toLowerCase())
                ? 2
                : 0) +
              (a.description.toLowerCase().includes(searchQuery.toLowerCase())
                ? 1
                : 0)
            : 0;
          const bRelevance = searchQuery
            ? (b.title.toLowerCase().includes(searchQuery.toLowerCase())
                ? 3
                : 0) +
              (b.company.toLowerCase().includes(searchQuery.toLowerCase())
                ? 2
                : 0) +
              (b.description.toLowerCase().includes(searchQuery.toLowerCase())
                ? 1
                : 0)
            : 0;
          comparison = bRelevance - aRelevance;
          break;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });
  };

  const sortedJobs = sortJobs(jobs);
  const totalPages = Math.ceil(sortedJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const paginatedJobs = sortedJobs.slice(startIndex, startIndex + jobsPerPage);

  const handleSortChange = (newSortBy: SortOption) => {
    if (sortBy === newSortBy) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(newSortBy);
      setSortOrder("desc");
    }
    setCurrentPage(1);
  };

  const getSortLabel = (option: SortOption) => {
    switch (option) {
      case "relevance":
        return "관련성";
      case "date":
        return "등록일";
      case "salary":
        return "연봉";
      case "applicants":
        return "지원자 수";
      default:
        return "관련성";
    }
  };

  return (
    <div>
      {/* 헤더 */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
        <div className="mb-4 sm:mb-0">
          <h2 className="text-xl font-semibold text-gray-900">
            채용공고 {jobs.length}개
          </h2>
          {searchQuery && (
            <p className="text-sm text-gray-600 mt-1">
              "{searchQuery}" 검색 결과
            </p>
          )}
        </div>

        <div className="flex items-center gap-4">
          {/* 정렬 옵션 */}
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">정렬:</span>
            <div className="flex border border-gray-300 rounded-lg overflow-hidden">
              {(
                ["relevance", "date", "salary", "applicants"] as SortOption[]
              ).map((option) => (
                <button
                  key={option}
                  onClick={() => handleSortChange(option)}
                  className={`px-3 py-2 text-sm font-medium transition-colors ${
                    sortBy === option
                      ? "bg-blue-600 text-white"
                      : "bg-white text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {getSortLabel(option)}
                </button>
              ))}
            </div>
            <button
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="p-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {sortOrder === "asc" ? (
                <SortAsc className="w-4 h-4 text-gray-600" />
              ) : (
                <SortDesc className="w-4 h-4 text-gray-600" />
              )}
            </button>
          </div>

          {/* 보기 모드 */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode("grid")}
              className={`p-2 transition-colors ${
                viewMode === "grid"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <Grid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode("list")}
              className={`p-2 transition-colors ${
                viewMode === "list"
                  ? "bg-blue-600 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-50"
              }`}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* 채용공고 목록 */}
      {paginatedJobs.length > 0 ? (
        <>
          <div
            className={`grid gap-6 ${
              viewMode === "grid"
                ? "grid-cols-1 md:grid-cols-2 lg:grid-cols-3"
                : "grid-cols-1"
            }`}
          >
            {paginatedJobs.map((job) => (
              <JobCard
                key={job.id}
                job={job}
                onBookmark={onBookmark}
                viewMode={viewMode}
              />
            ))}
          </div>

          {/* 페이지네이션 */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-8">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                이전
              </button>

              {[...Array(totalPages)].map((_, index) => {
                const page = index + 1;
                const isCurrentPage = page === currentPage;
                const isNearCurrentPage = Math.abs(page - currentPage) <= 2;
                const isFirstPage = page === 1;
                const isLastPage = page === totalPages;

                if (!isNearCurrentPage && !isFirstPage && !isLastPage) {
                  if (page === 2 || page === totalPages - 1) {
                    return (
                      <span key={page} className="px-2 text-gray-400">
                        ...
                      </span>
                    );
                  }
                  return null;
                }

                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 border rounded-lg text-sm font-medium transition-colors ${
                      isCurrentPage
                        ? "bg-blue-600 text-white border-blue-600"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    {page}
                  </button>
                );
              })}

              <button
                onClick={() =>
                  setCurrentPage(Math.min(totalPages, currentPage + 1))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                다음
              </button>
            </div>
          )}
        </>
      ) : (
        <div className="text-center py-12">
          <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            검색 결과가 없습니다
          </h3>
          <p className="text-gray-600 mb-4">
            다른 검색어나 필터를 시도해보세요
          </p>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
            필터 초기화
          </button>
        </div>
      )}
    </div>
  );
};

export default JobList;
